import React, { createContext, useContext, useState, useEffect } from 'react';
import authService, { User, LoginCredentials, RegisterData } from '../services/auth';

interface AuthContextType {
    user: User | null;
    isLoading: boolean;
    error: string | null;
    login: (credentials: LoginCredentials) => Promise<void>;
    register: (data: RegisterData) => Promise<void>;
    logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const initAuth = async () => {
            const token = localStorage.getItem('token');
            if (token) {
                try {
                    authService.setAuthToken(token);
                    const user = await authService.getCurrentUser();
                    setUser(user);
                } catch (error) {
                    console.error('Failed to restore auth state:', error);
                    authService.setAuthToken(null);
                }
            }
            setIsLoading(false);
        };

        initAuth();
    }, []);

    const login = async (credentials: LoginCredentials) => {
        try {
            setError(null);
            const { token, user } = await authService.login(credentials);
            authService.setAuthToken(token);
            setUser(user);
        } catch (error: any) {
            setError(error.response?.data?.message || 'Failed to login');
            throw error;
        }
    };

    const register = async (data: RegisterData) => {
        try {
            setError(null);
            const { token, user } = await authService.register(data);
            authService.setAuthToken(token);
            setUser(user);
        } catch (error: any) {
            setError(error.response?.data?.message || 'Failed to register');
            throw error;
        }
    };

    const logout = () => {
        authService.setAuthToken(null);
        setUser(null);
    };

    return (
        <AuthContext.Provider value={{ user, isLoading, error, login, register, logout }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export default AuthContext; 